import { useQuery, gql } from '@apollo/client';
import React, { useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { set, lensPath } from 'ramda';
import { hrefFor } from '../../routers';
import { PRODUCT_LIST_PAGE_RESULT_FRAGMENT, ProductListing } from '../../components/ProductListing';
import { usePLPParams } from '../../hooks/usePLPParams';
import { MODULE_SECTION_FRAGMENT, ModuleSection } from '../../components/ModuleSection';
import { PAGE_FILTER_FRAGMENT } from '../../components/PageFilter';
import {
  DesignerPageQuery,
  DesignerPageQueryVariables
} from '../../generated/types/DesignerPageQuery';
import { findSectionModules } from '../../lib/findSectionModules';
import { titleize } from '../../lib/titleize';
import { formatVertical } from '../../lib/strings/formatVertical';
import { NotFoundPage } from '../NotFoundPage';

export const DESIGNER_PAGE_QUERY = gql`
  query DesignerPageQuery(
    $after: String!
    $countryCode: String
    $availability: [AvailabilityInput]
    $filterColors: [String]
    $materials: [String]
    $designers: [String]
    $sizes: [String]
    $categoryPath: String
    $attributes: [String]
    $onSale: Boolean
    $priceRange: PriceRange
    $saleTags: [String]
    $slug: String!
  ) {
    displayPage(slug: $slug) {
      slug
      name
      targetBuckets {
        name
        modules {
          ...ModuleSectionFragment
        }
      }
    }

    designerCollection: designer_collection(
      slug: $slug
      input: {
        country_code: $countryCode
        availability: $availability
        filter_colors: $filterColors
        materials: $materials
        designers: $designers
        sizes: $sizes
        category_path: $categoryPath
        attributes: $attributes
        on_sale: $onSale
        no_sale: true
        price_range: $priceRange
        sale_tags: $saleTags
      }
    ) {
      pageName: page_name
      ...ProductListPageResultFragment
      ...PageFilterFragment
    }
  }
  ${MODULE_SECTION_FRAGMENT}
  ${PRODUCT_LIST_PAGE_RESULT_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
`;

// TODO: Why do multiple designers sometimes get sent back in the filters?
export const DesignerPage: React.FC = () => {
  const {
    params: { vertical, category, subcategory, designerSlug },
    queryVariables
  } = usePLPParams();
  const { data, loading, error } = useQuery<DesignerPageQuery, DesignerPageQueryVariables>(
    DESIGNER_PAGE_QUERY,
    { variables: queryVariables }
  );
  const topModules = useMemo(() => findSectionModules(data, 'top'), [data]);
  const headerModules = useMemo(() => findSectionModules(data, 'header'), [data]);
  const bottomModules = useMemo(() => findSectionModules(data, 'bottom'), [data]);
  const firstAttribute = queryVariables.attributes?.[0];
  const designerName = data?.designerCollection?.pageName || '';
  const categoryTree = data?.designerCollection?.categoryTree;
  const categoryText = firstAttribute || subcategory || category;
  const dataWithoutDesigners = data && set(lensPath(['designerCollection', 'designers']), [], data);
  const title = useMemo(() => {
    return `${designerName} ${categoryText || 'Fashion Collections'} for ${vertical}`;
  }, [categoryText, vertical, designerName]);

  const headerText = useMemo(() => {
    return categoryText
      ? `${designerName} ${titleize(categoryText)} for ${titleize(vertical)}`
      : `${designerName} for ${titleize(vertical)}`;
  }, [categoryText, vertical, designerName]);

  const description = `Shop new ${formatVertical(vertical)} ${designerName} ${
    categoryText || 'clothing'
  } and discover the latest collections on Moda Operandi.`;

  if (vertical === 'men') {
    return <Redirect to={hrefFor.HomePage()} />;
  }

  // if there are no results for the vertical, redirect to the first available vertical
  if (
    categoryTree?.children?.length &&
    !categoryTree.children.find(child => child?.slug === vertical)
  )
    return (
      <Redirect
        to={hrefFor.DesignerPage({
          designerSlug: designerSlug as string,
          vertical: categoryTree?.children[0]?.slug as string
        })}
      />
    );

  if (data && !data.designerCollection) return <NotFoundPage />;

  return (
    <>
      {topModules && <ModuleSection modules={topModules.modules} />}
      {headerModules && <ModuleSection modules={headerModules.modules} />}

      <ProductListing
        title={title}
        description={description}
        headerText={headerText}
        queryResult={dataWithoutDesigners?.designerCollection}
        error={error}
        loading={loading}
      />

      {bottomModules && <ModuleSection modules={bottomModules.modules} />}
    </>
  );
};
